import { h, Component, Host, State, Element, Prop, Watch, Fragment, Listen, Event, EventEmitter } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import {
  PageHeaderSchemeDefinition,
  PageHeaderSchemeName,
  ColorBars,
  PageHeaderHeadlinePositions
} from "./constants/page-header-types";
import {
  PAGE_HEADER_SCHEME_CONFIG,
  PAGE_HEADER_SCHEME_MAP,
  PAGE_HEADER_POSITION_TOP,
  PAGE_HEADER_POSITION_BOTTOM
} from "./constants/page-header-constants";
import { isMobile } from "../../utils/screen-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { getFirstNestedChild, getNestedChildrenOf } from "../../utils/dom-helper";
import { createButtonMapDefinition } from "../../utils/scheme-helper";
import { BackgroundColor } from "../background/constants/background-types";
import { HeadlineTag } from "../headline/constants/headline-types";
import { useTheRightHtmlTagForSubheadline } from "../../utils/headline-helper";
import { PageHeaderSchemeConfiguration } from "../../constants/colors";
import { setCssVariable } from "../../utils/css-variables";
import {
  PageHeaderAlignment,
  PAGE_HEADER_CONTENT_ALIGNMENT,
  PAGE_HEADER_DEFAULT_ALIGNMENT,
  PAGE_HEADER_HEADLINE_ALIGNMENT,
  PAGE_HEADER_HEADLINE_SCHEME_SHADOW_MAP,
  PAGE_HEADER_HEADLINE_SIZE,
  PAGE_HEADER_SUBHEADLINE_SIZE,
  PAGE_HEADER_DEFAULT_ENABLING_CONTRAST_BARS,
  PAGE_HEADER_DEFAULT_VIOLATOR_HORIZONTAL_POSITION,
  PageHeaderViolatorHorizontalPosition
} from "../../constants/global-constants";

/**
 * @slot header-image - Holds a single eon-ui-image element
 * @slot header-headline - holds a single eon-ui-headline element only H1
 * @slot header-subheadline - holds a single eon-ui-headline as subheadline element only H5
 * @slot header-content - Holds any number of HTML elements
 * @slot header-cta - Holds a single eon-ui-button or eon-ui-link element
 */
@Component({
  tag: "eon-ui-page-header",
  styleUrl: "page-header.scss",
  shadow: true
})
export class PageHeader implements ColorableComponent<PageHeaderSchemeConfiguration<PageHeaderSchemeDefinition>> {
  schemeConfig: PageHeaderSchemeConfiguration<PageHeaderSchemeDefinition> = PAGE_HEADER_SCHEME_CONFIG;
  schemeMap = PAGE_HEADER_SCHEME_MAP;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: PageHeaderSchemeDefinition;
  @State() isMobile: boolean;
  @State() ctaLink: boolean;
  @State() subheadline: boolean;
  @State() content: boolean;
  @State() seoHeadlineTag: string;

  @Element() hostElement: HTMLEonUiPageHeaderElement;

  private headlines: HTMLEonUiHeadlineElement[];
  private hasViolator: boolean;
  private violatorSize: string;

  /**
   * Raised when the component has been loaded.
   */
  @Event() pageHeaderLoad: EventEmitter;

  /**
   * Raised when the CTA in Page Header has been clicked.
   */
  @Event() pageHeaderClick: EventEmitter;

  /**
   * Manages the activation of animation.
   */
  @Prop({ reflect: true }) animation: boolean = false;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: PageHeaderSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * Headline position can be adjusted
   * Top | Middle | Bottom
   */
  @Prop({ reflect: true }) headlinePosition: PageHeaderHeadlinePositions = PAGE_HEADER_POSITION_TOP;

  /**
   * Headline alignment can be adjusted
   */
  @Prop() headlineAlignment: PageHeaderAlignment = PAGE_HEADER_DEFAULT_ALIGNMENT;

  /**
   * One contrast bar option is provided:
   * Right - one wide & one narrow
   */
  @Prop({ reflect: true }) contrastBars: boolean = PAGE_HEADER_DEFAULT_ENABLING_CONTRAST_BARS;

  /**
   * Color Bar option are provided:
   * Left - single narrow bar
   */
  @Prop({ reflect: true, mutable: true }) colorBars: ColorBars;

  /**
   * Color Bars position are provided:
   * Left - single narrow bar with distant margin from left
   */
  @Prop({ reflect: true }) colorBarOffset: boolean = false;

  /**
   * showContent gives flexibility to the Author to show or hide slotted content if not provided
   */
  @Prop({ reflect: true }) showContent: boolean = true;

  /**
   * this property is used for SEO-Optimisation.<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for the name- and info-properties,<br>
   *   the HTML-Tag for the info (subheadline) is set automatically depending on this property (headline-html-tag  -1 ), e.g <br>
   *    name(Headline) is H2, then info(subheadline) is H3, name(Headline) is H4, then info(subheadline) is H5, and so on
   *
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * Defines the position of violator on X
   */
  // prettier-ignore
  @Prop() violatorHorizontalPosition: PageHeaderViolatorHorizontalPosition = PAGE_HEADER_DEFAULT_VIOLATOR_HORIZONTAL_POSITION;

  componentWillLoad() {
    this.hasViolator = !!this.hostElement.querySelector('[slot="violator"]');
    if (this.hasViolator) {
      this.violatorSize = (getFirstNestedChild(this.hostElement, "eon-ui-violator") as HTMLElement).getAttribute(
        "size"
      );
    }
    this.applyScheme();
    this.imageHeightOnViewport();
    this.handleContent();
    this.passSchemeToChildren();
    this.handleHeadlineTag();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.handleColorBarsStyles();
    this.pageHeaderLoad.emit();
    this.enterViewport(this.hostElement);
  }

  @Listen("buttonClick")
  @Listen("linkClick")
  handleCTAOnClick(event) {
    this.showContent && this.pageHeaderClick.emit(event);
  }

  @Watch("animation")
  applyAnimation() {
    this.toggleAnimation(this.animation, "eonui-wide-contrast-bar");
    this.toggleAnimation(this.animation, "eonui-narrow-contrast-bar");
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.backgroundColor;
  }

  @Watch("colorBars")
  handleColorBarsStyles() {
    if (!!this.colorBars) {
      setCssVariable(this.hostElement, "--currentColorBar", `var(--eon-page-header-color-bar-${this.colorBars})`);
    }
  }

  @Watch("headlineHtmlTag")
  handleHeadlineTag() {
    this.headlines = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-headline"));
    this.headlines.forEach((headline) => {
      if (headline.getAttribute("slot") === "header-headline") {
        if (!!this.headlineHtmlTag) {
          headline.setAttribute("headline-html-tag", this.headlineHtmlTag);
        }
        headline.setAttribute("size", PAGE_HEADER_HEADLINE_SIZE);
        headline.setAttribute("alignment", PAGE_HEADER_HEADLINE_ALIGNMENT);
      } else if (headline.getAttribute("slot") === "header-subheadline") {
        if (!!this.headlineHtmlTag) {
          headline.setAttribute("headline-html-tag", useTheRightHtmlTagForSubheadline(this.headlineHtmlTag));
        }
        headline.setAttribute("size", PAGE_HEADER_SUBHEADLINE_SIZE);
        headline.setAttribute("alignment", PAGE_HEADER_CONTENT_ALIGNMENT);
      }
    });
  }

  handleContent() {
    const button: HTMLEonUiButtonElement = getFirstNestedChild(this.hostElement, "eon-ui-button");
    const link: HTMLEonUiLinkElement = getFirstNestedChild(this.hostElement, "eon-ui-link");
    this.ctaLink = button || link ? true : false;
    const contentAvailable = this.hostElement.querySelectorAll('[slot="header-content"]');
    this.content = contentAvailable && contentAvailable.length > 0;
    const subheadlineAvailable = this.hostElement.querySelectorAll('[slot="header-subheadline"]');
    this.subheadline = subheadlineAvailable && subheadlineAvailable.length > 0;
  }

  @Listen("resize", { target: "window" })
  imageHeightOnViewport() {
    this.isMobile = isMobile();
    const image: HTMLEonUiImageElement = getFirstNestedChild(this.hostElement, "eon-ui-image");
    const headlineH1: HTMLEonUiHeadlineElement = getFirstNestedChild(this.hostElement, "eon-ui-headline");
    if (image) {
      if (headlineH1?.getAttribute("slot") === "header-headline") {
        PAGE_HEADER_HEADLINE_SCHEME_SHADOW_MAP.map((item) => {
          if (Object.keys(item)[0] === headlineH1?.scheme) {
            headlineH1.setAttribute("text-shadow", `${Object.values(item)[0]}`);
          }
        });
      }
      image.setAttribute("use-as-background", "true");
      this.isMobile && image.setAttribute("height", "320px");
    }
  }

  @Watch("scheme")
  // This method is responsible to keep slotted components in sync with page-header scheme
  // when it changes during the life of the component. This mostly applies to storybook controls in
  // canvas tab where changes to props don't trigger component reloading (In docs tab it does).
  passSchemeToChildren() {
    if (!!this.scheme) {
      const backgroundColor = this.innerBackground;
      const buttonSchemeMapDefinition = createButtonMapDefinition();
      let buttons = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-button"));
      buttons.forEach((slotButton) =>
        slotButton.setAttribute("scheme", buttonSchemeMapDefinition[backgroundColor as BackgroundColor])
      );
    }
  }

  private enterViewport(element: HTMLElement) {
    if (this.animation === false) {
      return;
    }

    if (window && window.IntersectionObserver) {
      const pageHeaderObserver = new window.IntersectionObserver((entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio > 0) {
            this.handleEnterViewport();
          }
        });
      });
      pageHeaderObserver.observe(element);
    }
  }

  private handleEnterViewport() {
    this.toggleAnimation(true, "eonui-wide-contrast-bar");
    this.toggleAnimation(true, "eonui-narrow-contrast-bar");
  }

  private toggleAnimation(animation, selectorName) {
    const elementWideContrastBar = this.hostElement.shadowRoot
      .querySelector("eon-ui-background")
      .querySelector(`.${selectorName}`);
    elementWideContrastBar.classList.remove(`${selectorName}-animation`);
    if (animation) {
      /*
       * This timeout secures the direct classList adding after remove when component scrolls into viewport.
       */
      setTimeout(() => {
        elementWideContrastBar.classList.add(`${selectorName}-animation`);
      }, 15);
    }
  }

  render() {
    return (
      <Host>
        <eon-ui-background color={this.activeScheme.backgroundColor} invisible>
          <div
            class={pfx({
              "page-header-wrapper": true,
              "has-colorbar-offset": this.colorBars && this.colorBarOffset
            })}
          >
            <div class={pfx("image-headline-container")}>
              <div
                class={pfx({
                  ["image-section"]: true
                })}
              >
                <slot name="header-image"></slot>
                {!!this.colorBars && !this.isMobile && (
                  <div
                    class={pfx({
                      [`color-bars ${this.colorBars}`]: this.colorBars,
                      ["offset-bar"]: this.colorBarOffset
                    })}
                  ></div>
                )}
                <div
                  class={pfx({
                    "header-image-content": true,
                    [this.headlineAlignment]: true,
                    "has-violator": !!this.hasViolator,
                    [`violator-${this.violatorSize}`]: !!this.violatorSize
                  })}
                >
                  {this.hasViolator && (
                    <div
                      class={pfx({
                        "page-header-violator": true,
                        [this.violatorHorizontalPosition]: !!this.violatorHorizontalPosition
                      })}
                    >
                      <slot name="violator"></slot>
                    </div>
                  )}
                  <div
                    class={pfx({
                      ["headline-section"]: true,
                      [`${this.isMobile ? PAGE_HEADER_POSITION_BOTTOM : this.headlinePosition}`]: this.headlinePosition
                    })}
                  >
                    <div
                      class={pfx({
                        ["headline-section__inner"]: true
                      })}
                    >
                      <slot name="header-headline"></slot>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class={pfx("content-section")}>
              {this.contrastBars && (
                <Fragment>
                  <div class={pfx("content-contrast-bar")}>
                    <div class={pfx({ ["wide-contrast-bar"]: true })}></div>
                    <div class={pfx({ ["narrow-contrast-bar"]: true })}></div>
                  </div>
                </Fragment>
              )}
              <div
                class={pfx({
                  ["content-background"]: true
                })}
              ></div>
              {this.showContent && (
                <div
                  class={pfx({
                    ["content"]: true,
                    [`alignment-${PAGE_HEADER_CONTENT_ALIGNMENT}`]: true
                  })}
                >
                  {this.subheadline && (
                    <div class={pfx("subheadline")}>
                      <slot name="header-subheadline"></slot>
                    </div>
                  )}
                  {this.content && (
                    <div class={pfx({ "sub-content": true, ["no-subheadline"]: !this.subheadline })}>
                      <slot name="header-content"></slot>
                    </div>
                  )}
                  {this.ctaLink && (
                    <div class={pfx("cta-content")}>
                      <slot name="header-cta"></slot>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </eon-ui-background>
      </Host>
    );
  }
}
